import { useContext } from 'react';
import type { ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useChapterContent } from '@openclassrooms/data-connect-web/chapters';
import { getCurrentChapter } from '@openclassrooms/ui-web/table-of-content';
import { CourseStatus } from '@openclassrooms/sdk-web';
import CourseChapterContext from './courseChapterContext';
import courseContext from './courseContext';
import { useErrorHandlers } from './errorHandlerContext';

export interface CourseChapterContextProviderProps {
  children: ReactNode;
}

export default function CourseChapterContextProvider({
  children,
}: CourseChapterContextProviderProps) {
  const { courseId, flatToc } = useContext(courseContext);
  const urlParams = useParams<{ chapterIdSlug: string }>();
  const { onError } = useErrorHandlers();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const courseStatus = searchParams.get('status') as CourseStatus | undefined;

  const { chapterIdSlug } = urlParams;
  const [chapterId] = chapterIdSlug.split(/-(.+)/);

  const { data: chapterContent, error: chapterContentError } = useChapterContent(
    {
      courseId,
      chapterId: Number(chapterId),
      queryParams: {
        status: courseStatus,
      },
    },
    { enabled: Boolean(courseId && chapterId) }
  );

  const currentChapter = getCurrentChapter(flatToc, Number(chapterId));

  if (chapterContentError) {
    onError(chapterContentError);
    return null;
  }

  return (
    <CourseChapterContext.Provider
      value={{
        currentChapterId: Number(chapterId),
        currentChapter,
        chapterContent,
      }}
    >
      {children}
    </CourseChapterContext.Provider>
  );
}
